/**
 * Transcript checkpoint store — IndexedDB incremental persistence for
 * long file-mode Whisper transcriptions.
 *
 * WHY
 *   Bug-report 2026-05-28 (sid 38fdd911, /ru/audio-to-text): user dropped a
 *   279 MB / ~2 h MP3, the single-thread WASM transcription ran for ~200 min,
 *   she stepped away, came back to an empty page — no transcript, no error.
 *   Prod telemetry showed a 3.5 h gap with ZERO events: the file-mode pipeline
 *   built the transcript only from the worker's final `result` message and
 *   threw away every streamed `partial_text` / `chunk_done`. Any interruption
 *   (tab discard, OOM, reload, sleep) lost the whole job.
 *
 *   This store persists the partial transcript (segments + plain text + meta)
 *   to IndexedDB as each audio chunk finalizes, keyed by a stable file
 *   fingerprint. On the next visit the tool can surface a recovery banner and
 *   hand the user back their work — and, because we persist the last decoded
 *   audio timestamp, optionally resume transcribing only the remaining tail.
 *
 *   Sibling to stt-recovery-store.js (which persists mic-takeover audio Blobs).
 *   Kept separate because the payload is text (cheap, frequent writes) not a
 *   multi-hundred-MB Blob, and because this store is consumed by every Whisper
 *   file-transcription tool (audio-to-text, auto-captions, video-subtitles,
 *   interview-studio) — not just STT mic mode.
 *
 * API
 *   TranscriptCheckpointStore.isSupported()            // bool
 *   await TranscriptCheckpointStore.init()             // open DB
 *   TranscriptCheckpointStore.fingerprint(file, extra) // stable string key
 *   await TranscriptCheckpointStore.save(key, data)    // idempotent put (throttle in caller)
 *   await TranscriptCheckpointStore.get(key)           // Promise<checkpoint|null>
 *   await TranscriptCheckpointStore.listPending()      // Promise<Array<meta>> (no segments)
 *   await TranscriptCheckpointStore.discard(key)       // remove one
 *   await TranscriptCheckpointStore.purgeStale(maxAgeMs)  // remove old; returns count
 *   await TranscriptCheckpointStore.takeLost(origin)   // records the BROWSER removed since saved (once)
 *   await TranscriptCheckpointStore.protection()       // {bucket, persisted}
 *
 *   `data` shape (free-form, the tool owns it):
 *     { fileName, fileSize, lang, model, task, audioDuration, totalChunks,
 *       chunksDone, lastTimestamp, segments:[{text,timestamp,isFinal}],
 *       fullText, status:'in_progress'|'done', startedAt }
 */
(function (global) {
    'use strict';

    var DB_NAME    = 'transcript_checkpoints';
    var DB_VERSION = 1;
    var STORE_NAME = 'checkpoints';
    var _dbPromise = null;

    /* ── Where the work lives, and why it is not the site's default storage ──
       Support chat 640 (27.09.2026): two hours of censor edits on a one-hour
       video, the machine rebooted, and the work was gone — together with
       EVERYTHING the site kept in that browser (device_cap.su 306 MB → 0) while
       the login cookie lived on. That is Chromium's storage eviction, not a
       crash: below 2 GB free on the disk («should remain available») it deletes
       whole non-persistent storage buckets, least recently used first, 5 minutes
       after the browser starts and every 30 minutes after that — and a page
       being open is no exception (QuotaDatabase::GetBucketsForEviction). Our
       default bucket also carries the disposable gigabytes (picked-file copies
       in OPFS, model caches), so it is the first thing that pays for a full disk,
       and a transcript that cost ten minutes of recognition and hours of a
       person's edits went down with a cache we could download again.
       Reproduced in real Chrome (tests/playwright/work-survives-disk-pressure.mjs):
       the old store loses the record with the copy; a separate bucket named
       `work` keeps it while the copy is freed, and a PERSISTED `work` bucket
       keeps it whatever the shortage.
       So in Chromium the checkpoints live in their own bucket, opened with
       `persisted: true`. That flag only takes when the SITE holds the
       persistent-storage permission, and only navigator.storage.persist() asks
       for it (Chromium's silent decision: engagement, bookmark, installed app;
       no prompt) — see ensurePersisted(); asked again while there is work to
       protect, because a busy afternoon raises the engagement that decides it.
       Elsewhere (Firefox asks the person, WebKit has no buckets) nothing
       changes: the default IndexedDB, as before. */
    var BUCKET_NAME = 'work';
    var _bucket = null;          // StorageBucket, when the browser has them and opened one
    var _mode = '';              // 'b' bucket | 'd' default — which storage THIS page reads

    function isSupported() {
        return typeof global.indexedDB !== 'undefined';
    }

    function bucketsSupported() {
        var n = global.navigator;
        return !!(n && n.storageBuckets && typeof n.storageBuckets.open === 'function');
    }

    function openDb(factory, create) {
        return new Promise(function (resolve, reject) {
            var req = factory.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = function () {
                var db = req.result;
                if (!create) { try { req.transaction.abort(); } catch (_) {} return; }
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                }
            };
            req.onsuccess = function () { resolve(req.result); };
            req.onerror   = function () { reject(req.error || new Error('IDB open failed')); };
            req.onblocked = function () { reject(new Error('IDB blocked')); };
        });
    }

    /* Records written before the bucket existed (or by a tab still running the
       previous script) sit in the default storage: carry them over, newest wins,
       then remove them there. Best-effort — a failure leaves them where they were
       and the next page tries again. The default database is only opened when it
       exists, so a fresh browser does not grow an empty one on every load. */
    function migrateLegacy(db) {
        var list = global.indexedDB && global.indexedDB.databases ? global.indexedDB.databases() : Promise.resolve([{ name: DB_NAME }]);
        return Promise.resolve(list).then(function (dbs) {
            if (!(dbs || []).some(function (d) { return d && d.name === DB_NAME; })) return 0;
            return openDb(global.indexedDB, false).then(function (old) {
                if (!old.objectStoreNames.contains(STORE_NAME)) { old.close(); return 0; }
                return new Promise(function (resolve) {
                    var rows = [];
                    var rt = old.transaction(STORE_NAME, 'readonly');
                    rt.objectStore(STORE_NAME).openCursor().onsuccess = function (e) {
                        var cur = e.target.result;
                        if (cur) { rows.push(cur.value); cur.continue(); }
                    };
                    rt.oncomplete = function () {
                        if (!rows.length) { old.close(); resolve(0); return; }
                        var wt = db.transaction(STORE_NAME, 'readwrite');
                        var st = wt.objectStore(STORE_NAME);
                        rows.forEach(function (row) {
                            var g = st.get(row.key);
                            g.onsuccess = function () {
                                var have = g.result;
                                if (!have || (have.updatedAt || 0) < (row.updatedAt || 0)) st.put(row);
                            };
                        });
                        wt.oncomplete = function () {
                            var dt = old.transaction(STORE_NAME, 'readwrite');
                            var ds = dt.objectStore(STORE_NAME);
                            rows.forEach(function (row) { ds.delete(row.key); });
                            dt.oncomplete = function () { old.close(); resolve(rows.length); };
                            dt.onerror = dt.onabort = function () { old.close(); resolve(0); };
                        };
                        wt.onerror = wt.onabort = function () { old.close(); resolve(0); };
                    };
                    rt.onerror = rt.onabort = function () { old.close(); resolve(0); };
                });
            });
        }).catch(function () { return 0; });
    }

    function open() {
        if (_dbPromise) return _dbPromise;
        if (!isSupported()) {
            _dbPromise = Promise.reject(new Error('IndexedDB unavailable'));
            return _dbPromise;
        }
        var viaDefault = function () {
            _bucket = null; _mode = 'd';
            return openDb(global.indexedDB, true);
        };
        _dbPromise = bucketsSupported()
            ? global.navigator.storageBuckets.open(BUCKET_NAME, { persisted: true, durability: 'strict' })
                .then(function (b) { return openDb(b.indexedDB, true).then(function (db) { _bucket = b; _mode = 'b'; return db; }); })
                .then(function (db) {
                    return migrateLegacy(db).then(function (n) {
                        if (n) report('work_store_migrated', { n: n });
                        return db;
                    });
                }, viaDefault)
            : viaDefault();
        return _dbPromise;
    }

    function report(f, data) {
        try {
            if (typeof global.__ae === 'function') global.__ae('feature_use', Object.assign({ f: f }, data || {}));
        } catch (_) {}
    }

    /* Protection is asked for while there is something worth protecting, not on
       every page view: once when the first real record of this page is written,
       and again after a quarter of an hour of work — Chromium's answer follows
       site engagement, which that work itself raises. The verdict is measured
       (`work_store`): how much of the work lives in storage the browser will not
       take back under disk pressure. */
    var _persistAskedAt = 0, _persistState = '';
    function worthKeeping(data) {
        return !!(data && (data.decisions > 0 || data.status === 'done' || (data.chunksDone || 0) > 0));
    }
    var _defaultReported = false;
    function ensurePersisted(data) {
        if (!worthKeeping(data)) return;
        if (_mode === 'd') {
            // no bucket here: say so once, with what the origin already has
            if (_defaultReported) return;
            _defaultReported = true;
            var st = global.navigator && global.navigator.storage;
            Promise.resolve(st && st.persisted ? st.persisted() : false).then(function (p) {
                report('work_store', { bucket: 0, persisted: p ? 1 : 0, via: 'default', origin: String(data.origin || '').slice(0, 32) });
            }).catch(function () {});
            return;
        }
        if (_persistState === 'granted') return;
        var now = Date.now();
        if (_persistAskedAt && now - _persistAskedAt < 15 * 60000) return;
        var first = !_persistAskedAt;
        _persistAskedAt = now;
        var b = _bucket;
        if (!b) return;
        /* Only navigator.storage.persist() ASKS. It runs Chromium's silent decision
           (engagement, bookmark, installed app, notifications) — StorageManager::persist
           → RequestPermission(PERSISTENT_STORAGE). StorageBucket.persist() merely reads a
           permission the site already holds (BucketHost::Persist → GetPermissionStatus)
           and never grants one. Until 29.09.2026 this store asked the bucket alone: its
           work was persistent for 12 of ~380 saves (3 %), all on sites that already held
           the permission, while /screen-recorder, asking the storage manager, gets it for
           40–50 % of the same browsers. Granted to the site, the permission also makes
           the default bucket persistent; the picked-file copies live in their own
           non-persistent `copies` bucket and stay evictable. This branch exists only
           where buckets do (Chromium) — there the request is silent; Firefox, which
           shows a prompt for it, takes the default branch above. */
        var sm = global.navigator && global.navigator.storage;
        Promise.resolve(b.persisted()).then(function (p) {
            if (p) return 'already';
            return Promise.resolve(sm && typeof sm.persist === 'function' ? sm.persist() : false).then(function (site) {
                if (!site) return 'denied';
                return Promise.resolve(b.persist()).then(function (g) { return g ? 'granted' : 'denied_bucket'; });
            });
        }).then(function (state) {
            var prev = _persistState;
            _persistState = state === 'already' ? 'granted' : state;
            if (first || state !== prev) report('work_store', { bucket: 1, persisted: _persistState === 'granted' ? 1 : 0, via: state, origin: String(data.origin || '').slice(0, 32) });
        }).catch(function () {});
    }

    /* ── Knowing that the work was taken away ──
       Eviction is silent: the next visit finds an empty store, and the page can
       only say nothing — the person sees the upload zone and concludes the
       «saved automatically» promise was false. localStorage is not part of what
       Chromium's evictor deletes (the stand above: the IndexedDB record and the
       OPFS copy gone, a localStorage marker still there — as the login cookie
       was in chat 640), so a small index of what we saved lives there. A record
       the index names and the store no longer has was not discarded by us
       (discard and purge keep the index in step): the browser removed it. That
       becomes an explanation on the tool's page (takeLost) and a counted event
       (`work_store_lost`), where until now there was neither.
       Entries carry which storage they were written to, so a page that could
       not open the bucket does not report the bucket's records as lost. */
    var INDEX_KEY = 'tcs-index-v1';
    var INDEX_MAX = 60;
    var INDEX_MAX_AGE = 45 * 86400000;
    function readIndex() {
        var ix = {};
        try { ix = JSON.parse(global.localStorage.getItem(INDEX_KEY) || '{}') || {}; } catch (_) { ix = {}; }
        var cut = Date.now() - INDEX_MAX_AGE;
        Object.keys(ix).forEach(function (k) { if (!ix[k] || (ix[k].u || 0) < cut) delete ix[k]; });
        return ix;
    }
    function writeIndex(ix) {
        try {
            var keys = Object.keys(ix);
            if (keys.length > INDEX_MAX) {
                keys.sort(function (a, b) { return (ix[a].u || 0) - (ix[b].u || 0); });
                keys.slice(0, keys.length - INDEX_MAX).forEach(function (k) { delete ix[k]; });
            }
            if (Object.keys(ix).length) global.localStorage.setItem(INDEX_KEY, JSON.stringify(ix));
            else global.localStorage.removeItem(INDEX_KEY);
        } catch (_) {}
    }
    function indexPut(entry) {
        if (!_mode || !worthKeeping(entry)) return;
        var ix = readIndex();
        ix[entry.key] = {
            o: String(entry.origin || '').slice(0, 32), n: entry.decisions || 0,
            f: String(entry.fileName || '').slice(0, 80), u: entry.updatedAt || Date.now(), m: _mode
        };
        writeIndex(ix);
    }
    function indexDrop(keys) {
        if (!keys || !keys.length) return;
        var ix = readIndex(), changed = false;
        keys.forEach(function (k) { if (ix[k]) { delete ix[k]; changed = true; } });
        if (changed) writeIndex(ix);
    }
    // index entries of THIS storage whose record is gone
    function missingFromIndex(filter) {
        return open().then(function () {
            var ix = readIndex();
            var keys = Object.keys(ix).filter(function (k) { return ix[k].m === _mode && (!filter || filter(ix[k])); });
            if (!keys.length) return [];
            return tx('readonly', function (store) {
                var r = { value: [] };
                keys.forEach(function (k) {
                    var g = store.getKey(k);
                    g.onsuccess = function () { if (g.result === undefined) r.value.push(Object.assign({ key: k }, ix[k])); };
                });
                return r;
            });
        });
    }
    /* Once a page: count what the browser took (every tool's records, not only
       the visited tool's), without consuming the entries — the owner tool still
       gets to explain it to the person on its own page. */
    var _lostSwept = false;
    function sweepLost() {
        if (_lostSwept) return;
        _lostSwept = true;
        missingFromIndex(function (e) { return !e.r; }).then(function (gone) {
            if (!gone.length) return;
            var ix = readIndex();
            gone.slice(0, 5).forEach(function (e) {
                report('work_store_lost', { origin: e.o, n: e.n, age_h: Math.round((Date.now() - (e.u || 0)) / 3600000), m: e.m });
            });
            gone.forEach(function (e) { if (ix[e.key]) ix[e.key].r = 1; });
            writeIndex(ix);
        }).catch(function () {});
    }
    /* Records of `origin` the browser removed since they were saved — handed to
       the tool once, then forgotten. */
    function takeLost(origin) {
        return missingFromIndex(function (e) { return !origin || e.o === origin; }).then(function (gone) {
            indexDrop(gone.map(function (e) { return e.key; }));
            return gone.map(function (e) {
                return { key: e.key, origin: e.o, decisions: e.n, fileName: e.f, updatedAt: e.u };
            });
        });
    }

    function tx(mode, fn) {
        return open().then(function (db) {
            return new Promise(function (resolve, reject) {
                var t = db.transaction(STORE_NAME, mode);
                var store = t.objectStore(STORE_NAME);
                var result;
                try { result = fn(store); }
                catch (e) { reject(e); return; }
                t.oncomplete = function () { resolve(result && result.value !== undefined ? result.value : result); };
                t.onerror    = function () { reject(t.error || new Error('IDB tx failed')); };
                t.onabort    = function () { reject(t.error || new Error('IDB tx aborted')); };
            });
        });
    }

    /* Stable fingerprint for a File — name + size + lastModified is enough to
       identify the same upload across visits without reading the bytes (which
       for a 279 MB file would defeat the point). `extra` lets the caller fold
       in model/lang so a re-transcribe with a different model doesn't collide
       with an in-progress checkpoint for the same audio. */
    function fingerprint(file, extra) {
        if (!file) return '';
        var name = (file.name || 'audio').replace(/\s+/g, '_').slice(0, 80);
        var parts = [name, file.size || 0, file.lastModified || 0];
        if (extra) parts.push(String(extra));
        return parts.join('|');
    }

    /* Persist (or overwrite) a checkpoint. Idempotent on `key`. Caller is
       responsible for throttling writes (we don't want a put on every token —
       once per finalized chunk, or a few seconds, is right). */
    function save(key, data) {
        if (!key) return Promise.reject(new Error('key required'));
        var entry = Object.assign({}, data || {}, { key: key, updatedAt: Date.now() });
        return tx('readwrite', function (store) {
            var r = { value: undefined };
            var req = store.put(entry);
            req.onsuccess = function () { r.value = true; };
            return r;
        }).then(function (ok) {
            // the index names only what is really on disk: a refused write is no «loss»
            indexPut(entry);
            ensurePersisted(entry);
            sweepLost();
            return ok;
        });
    }

    function get(key) {
        if (!key) return Promise.reject(new Error('key required'));
        return tx('readonly', function (store) {
            var r = { value: null };
            var req = store.get(key);
            req.onsuccess = function () { r.value = req.result || null; };
            return r;
        });
    }

    /* List metadata for every checkpoint — strips the (potentially large)
       segments array + fullText to stay fast. Caller picks one then get()s it. */
    function listPending() {
        return tx('readonly', function (store) {
            var r = { value: [] };
            var req = store.openCursor();
            req.onsuccess = function (e) {
                var cur = e.target.result;
                if (!cur) return;
                var v = cur.value || {};
                r.value.push({
                    key:           v.key,
                    fileName:      v.fileName || '',
                    fileSize:      v.fileSize || 0,
                    lang:          v.lang || '',
                    model:         v.model || '',
                    audioDuration: v.audioDuration || 0,
                    lastTimestamp: v.lastTimestamp || 0,
                    chunksDone:    v.chunksDone || 0,
                    totalChunks:   v.totalChunks || 0,
                    status:        v.status || 'in_progress',
                    /* Откуда работа — диктовка или файл. Проекция здесь ЯВНАЯ,
                       и забытое поле не «пустое», а невидимое: фильтр по
                       origin молча считал все записи файловыми, и диктовка не
                       увидела ни одной своей. */
                    origin:        v.origin || '',
                    /* Забрал ли человек эту работу (скачал субтитры, прожёг
                       видео). Предлагать вернуть уже унесённое — навязчивость;
                       узнать об этом можно только отсюда, потому что проекция
                       явная. */
                    delivered:     v.delivered ? 1 : 0,
                    /* Сколько решений человека лежит в записи (цензор: включённые
                       и выключенные слова, нарисованные и сдвинутые заглушки) —
                       по нему /profanity-remover решает, есть ли что вернуть. */
                    decisions:     v.decisions || 0,
                    wordCount:     v.fullText ? v.fullText.split(/\s+/).filter(Boolean).length : 0,
                    updatedAt:     v.updatedAt || 0,
                });
                cur.continue();
            };
            return r;
        }).then(function (list) {
            // every tool looks here on load — the moment to notice what the browser took
            sweepLost();
            return list;
        });
    }

    function discard(key) {
        if (!key) return Promise.reject(new Error('key required'));
        return tx('readwrite', function (store) {
            var r = { value: undefined };
            store.delete(key);
            r.value = true;
            return r;
        }).then(function (ok) { indexDrop([key]); return ok; });
    }

    /* Auto-cleanup — entries older than maxAgeMs are silently dropped on init
       so a forgotten week-old partial doesn't surprise the user months later.
       Returns Promise<deletedCount>.

       The store is SHARED and each caller sweeps it with its own age: the
       speech workspace with 24 h, /auto-captions with 7 days. A record whose
       owner promises a longer life (/profanity-remover keeps a month of a
       person's censor edits) declares it as `keepMs`, and a neighbour's sweep
       honours it — otherwise one visit to /audio-to-text would silently erase
       hours of someone's work in another tool. */
    function purgeStale(maxAgeMs) {
        if (!maxAgeMs || maxAgeMs < 0) return Promise.resolve(0);
        var now = Date.now();
        var gone = [];
        return tx('readwrite', function (store) {
            var r = { value: 0 };
            var req = store.openCursor();
            req.onsuccess = function (e) {
                var cur = e.target.result;
                if (!cur) return;
                var v = cur.value || {};
                var age = Math.max(maxAgeMs, v.keepMs || 0);
                if ((v.updatedAt || 0) < now - age) { gone.push(v.key); cur.delete(); r.value++; }
                cur.continue();
            };
            return r;
        }).then(function (n) { indexDrop(gone); return n; });
    }

    function _wipeAll() {
        return tx('readwrite', function (store) {
            store.clear();
            return { value: true };
        }).then(function (ok) { try { global.localStorage.removeItem(INDEX_KEY); } catch (_) {} return ok; });
    }

    /* Which storage holds the work on this page and whether the browser has
       promised to keep it — for a tool that wants to say so, and for stands. */
    function protection() {
        return open().then(function () {
            if (_mode !== 'b' || !_bucket) return { bucket: false, persisted: false };
            return Promise.resolve(_bucket.persisted()).then(function (p) { return { bucket: true, persisted: !!p }; });
        });
    }

    global.TranscriptCheckpointStore = {
        isSupported: isSupported,
        init:        open,
        fingerprint: fingerprint,
        save:        save,
        get:         get,
        listPending: listPending,
        discard:     discard,
        purgeStale:  purgeStale,
        takeLost:    takeLost,
        protection:  protection,
        _wipeAll:    _wipeAll,
    };
})(typeof window !== 'undefined' ? window : this);
